import React, { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import {
    Container, Typography, Card, CardContent, Box,
    TextField, InputAdornment,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import AppReleaseQuery from 'services/graphQL/query/core/AppReleaseQuery';
import moment from 'moment';
import marked from 'marked';
import AppReleaseStyles from 'styles/modules/settings/AppReleaseStyles';
import InfiniteScroll from 'components/widgets/InfiniteScroll';
import { debounce } from 'lodash';

const useStyles = makeStyles((theme) => {
    const shared = AppReleaseStyles.shared(theme);
    const { colors } = shared;
    return {
        root: {
            backgroundColor: colors.slate[50],
            height: '100%',
            paddingTop: theme.spacing(4),
            paddingBottom: theme.spacing(2),
            display: 'flex',
            flexDirection: 'column',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: theme.spacing(6),
            flexShrink: 0,
        },
        searchField: shared.searchField,
        scrollContainer: {
            flex: 1,
            overflow: 'hidden',
        },
        timelineContainer: {
            position: 'relative',
            paddingLeft: theme.spacing(6),
            paddingBottom: theme.spacing(8),
            '&::before': {
                content: '""',
                position: 'absolute',
                left: 20,
                top: 0,
                bottom: 0,
                width: 2,
                backgroundColor: colors.slate[200],
            },
        },
        releaseItem: {
            position: 'relative',
            marginBottom: theme.spacing(8),
        },
        timelineDot: {
            position: 'absolute',
            left: -24 - 20,
            top: 8,
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: colors.blue[500],
            border: '3px solid #fff',
            boxShadow: `0 0 0 4px ${colors.blue[50]}`,
            zIndex: 1,
        },
        versionTitle: {
            fontSize: '1.8rem',
            fontWeight: 800,
            color: colors.slate[800],
            lineHeight: 1,
        },
        versionDate: {
            fontSize: '0.9rem',
            color: colors.slate[500],
            marginTop: theme.spacing(0.5),
            marginBottom: theme.spacing(2),
        },
        card: shared.card,
        cardTitle: {
            fontSize: '1.2rem',
            fontWeight: 700,
            color: colors.slate[800],
            marginBottom: theme.spacing(2),
        },
        content: shared.content,
    };
});

const ReleaseNotes = () => {
    const classes = useStyles();
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    const {
        data, loading, error, fetchMore,
    } = useQuery(AppReleaseQuery.GET_ALL_APP_RELEASES, {
        variables: { limit: 10, offset: 0, searchTerm: debouncedSearchTerm },
        notifyOnNetworkStatusChange: true,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSearch = useCallback(
        debounce((nextValue) => setDebouncedSearchTerm(nextValue), 500),
        [],
    );

    useEffect(() => {
        debouncedSearch(searchTerm);
        return () => {
            debouncedSearch.cancel();
        };
    }, [searchTerm, debouncedSearch]);

    if (error) {
        return (
            <Box mt={10}>
                <Typography color="error" align="center">
                    Error loading release notes
                </Typography>
            </Box>
        );
    }

    const releasesResponse = data?.getAllAppReleases;
    const releases = releasesResponse?.items ?? [];
    const totalCount = releasesResponse?.total ?? 0;

    const loadMore = () => {
        if (loading || releases.length >= totalCount) return;

        fetchMore({
            variables: {
                offset: releases.length,
            },
            updateQuery: (prev, { fetchMoreResult }) => {
                if (!fetchMoreResult) return prev;
                return {
                    getAllAppReleases: {
                        ...fetchMoreResult.getAllAppReleases,
                        items: [
                            ...prev.getAllAppReleases.items,
                            ...fetchMoreResult.getAllAppReleases.items,
                        ],
                    },
                };
            },
        });
    };

    return (
        <div className={classes.root}>
            <Container maxWidth="md">
                <Box className={classes.header}>
                    <Typography variant="h5" style={{ fontWeight: 700, color: '#1e293b' }}>
                        System Release Notes
                    </Typography>
                    <TextField
                        className={classes.searchField}
                        variant="outlined"
                        size="small"
                        placeholder="Search updates..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon style={{ color: '#94a3b8' }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
            </Container>

            <div className={classes.scrollContainer}>
                <InfiniteScroll
                    lengthRecord={releases.length}
                    totalRecord={totalCount}
                    loadMore={loadMore}
                    load={loading}
                    loadAtScrollPercent={80}
                    emptyRecordMessage={loading && releases.length === 0 ? '' : 'No updates found matching your search.'}
                >
                    <Container maxWidth="md">
                        <div className={classes.timelineContainer}>
                            {releases.map((release) => (
                                <div key={release.appReleaseId} className={classes.releaseItem}>
                                    <div className={classes.timelineDot} />
                                    <Typography className={classes.versionTitle}>
                                        {moment(release.releaseDate).format('MMMM D, YYYY')}
                                    </Typography>
                                    <Typography className={classes.versionDate}>
                                        {release.type}
                                    </Typography>

                                    <Card className={classes.card}>
                                        <CardContent style={{ padding: 32 }}>
                                            <Typography className={classes.cardTitle}>
                                                {release.title}
                                            </Typography>
                                            <div
                                                className={classes.content}
                                                // eslint-disable-next-line react/no-danger
                                                dangerouslySetInnerHTML={{ __html: marked(release.content) }}
                                            />
                                        </CardContent>
                                    </Card>
                                </div>
                            ))}
                        </div>
                    </Container>
                </InfiniteScroll>
            </div>
        </div>
    );
};

export default ReleaseNotes;
